import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Pagination, Space, Typography } from "antd"
import { t } from "i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import Export from "../../../../Components/Export"
import Filters from "../../../../Components/Filters"
import GlobalFilterTestData from "../../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../../Components/ManageColumns"
import Table, { itemRender } from "../../../../Components/Table"
import { serializeData } from "../../../../Utils/utils"
import { useUpdatePreferences } from "../../../User/hooks/useUpdatePreferences"
import {
  adminTableDefaultColumns,
  clearAdminTableFilters,
  pinnedCols,
  REFERRALS_ADMIN_TABLE_PREFERENCES_VERSION,
  resetAdminTableExportData,
  selectAreAdminTableFiltersDefault,
  setAdminTableColumns,
  setAdminTableFilters
} from "../../redux/referrals.slice"
import { exportAdminTable, getAdminTable } from "../../redux/referrals.thunks"
import AlertsChartModal from "./components/AlertsChartModal"
import RewardChartModal from "./components/RewardChartModal"
import { getActionColumn, getColumns } from "./components/Table/columns"
import { flattenReferralsData } from "./utils/flattenRefferalsData"

import "./styles.less"

const { Text } = Typography

const filtersIconsMap = {
  affiliate_email: "user",
  affiliate_full_name: "user",
  referral_full_name: "user",
  referral_email: "user",
  business_account_name: "business",
  transaction_timestamp: "calendar_days",
  business_account_created: "calendar_days",
  business_account_status: "pulse",
  referral_status: "pulse"
}

const pinnedColsNames = pinnedCols.map(
  (fieldId) => adminTableDefaultColumns.find((c) => c.field_id === fieldId).label
)

const PAGE_SIZE = 15

const AdminMainTable = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const { theme } = useSelector((state) => state.nav)
  const {
    adminTable,
    adminTableFetching,
    adminTableFilters,
    adminTableFilterOptions,
    adminTableVisibleFilters,
    adminTableExportFetching,
    adminTableExportData,
    adminTableColumns
  } = useSelector((state) => state.referrals)
  const { testData: test_data } = useSelector((state) => state.globalFilters)
  const isDefaultFilters = useSelector(selectAreAdminTableFiltersDefault)

  const [openRewardChart, setOpenRewardChart] = useState(false)
  const [openAlertsChart, setOpenAlertsChart] = useState(false)
  const [chartBaId, setChartBaId] = useState(null)
  const [chartUserId, setChartUserId] = useState(null)
  const [page, setPage] = useState(1)

  const preferencesData = useMemo(
    () => ({
      version: REFERRALS_ADMIN_TABLE_PREFERENCES_VERSION,
      adminTableFilters,
      adminTableColumns,
      adminTableVisibleFilters
    }),
    [adminTableFilters, adminTableColumns, adminTableVisibleFilters]
  )
  useUpdatePreferences("referrals-admin-table", preferencesData)

  const allFilters = adminTableDefaultColumns

  const scroll = useMemo(() => {
    return {
      x: "max-content",
      y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
      scrollToFirstRowOnChange: true
    }
  }, [])

  useEffect(() => {
    const params = serializeData(adminTableFilters) ?? {}
    dispatch(
      getAdminTable({
        ...params,
        test_data,
        page,
        page_size: PAGE_SIZE
      })
    )
  }, [adminTableFilters, test_data, page])

  const flattenTable = useMemo(() => flattenReferralsData(adminTable?.results || []), [adminTable])

  const onSelectRow = (record, type) => {
    const business_account_id = record?.business_account?.business_account_id
    if (type === "business_account") {
      if (business_account_id) {
        window.open(
          `${process.env.REACT_APP_BASE_URI}/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`
        )
      }
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "alerts") {
      setChartBaId(business_account_id)
      setOpenAlertsChart(true)
    } else if (type === "reward") {
      setChartBaId(business_account_id)
      setOpenRewardChart(true)
      setChartUserId(record?.user_id)
    } else if (type === "user_name") {
      navigate(`/referral/affiliate/${record?.user_id}`)
    } else if (type === "referral_ba_details") {
      navigate(`/referral/referral-ba/${business_account_id}?user_id=${record?.user_id}`)
    }
  }

  const columns = useMemo(() => {
    const colsMap = getColumns({ adminTable, onSelectRow: onSelectRow, theme })
    return [
      ...pinnedCols.map((colName) => colsMap?.[colName])?.filter((col) => col),
      ...adminTableColumns
        ?.filter((col) => col.active)
        ?.map((col) => colsMap?.[col.field_id])
        ?.filter((col) => col),
      getActionColumn({ onSelectRow })
    ]
  }, [theme, onSelectRow, adminTable, adminTableColumns])

  const setFilters = useCallback((filters, type) => {
    if (type === "changeVisible") {
      dispatch(
        setAdminTableFilters({
          visibleFilters: filters
        })
      )
    } else {
      dispatch(
        setAdminTableFilters({
          filters
        })
      )
    }
    setPage(1)
  }, [])

  const setColumns = useCallback(
    (columns) => dispatch(setAdminTableColumns({ columns })),
    []
  )

  const clearFilters = useCallback(() => {
    dispatch(clearAdminTableFilters())
  }, [])

  const handleExport = useCallback(
    (type) => {
      const params = serializeData(adminTableFilters) ?? {}
      dispatch(
        exportAdminTable({
          ...(type !== "all" && { ...params, test_data })
        })
      )
    },
    [adminTableFilters, test_data]
  )

  useEffect(() => {
    if (adminTableExportData) {
      notification.success({
        message: "Exporting",
        description:
          adminTableExportData?.message ??
          "Export in progress, the export file will be sent to your email once it’s done.",
        closeIcon: <Text>{t("Dismiss")}</Text>
      })
      dispatch(resetAdminTableExportData())
    }
  }, [adminTableExportData])

  return (
    <Flex className="referrals-admin-main-table" vertical gap={22}>
      <Flex gap={32} align="center" justify="space-between">
        <Filters
          visibleFilters={adminTableVisibleFilters}
          filterOptions={adminTableFilterOptions}
          allFilters={allFilters}
          filters={adminTableFilters}
          filtersType="referrals"
          onChangeFilter={setFilters}
          showClearBtn={!isDefaultFilters}
          onClearFilters={clearFilters}
          columnIconMap={filtersIconsMap}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={setPage} />
          <Export onExport={handleExport} isAdmin={true} loading={adminTableExportFetching} />
          <ManageColumn
            myColumns={adminTableColumns}
            onChangeColumns={setColumns}
            pinnedColumns={pinnedColsNames}
          />
        </Space>
      </Flex>

      <Flex vertical gap={16}>
        <Table
          rowKey={"row_id"}
          dataSource={flattenTable}
          columns={columns}
          loading={adminTableFetching}
          expandable={{ defaultExpandAllRows: true }}
          scroll={scroll}
          withOutPagination={true}
        />
        <div className="custom-table">
          <Pagination
            rootClassName={"ant-table-pagination ant-table-pagination-left"}
            onChange={(page) => setPage(page)}
            position={["none", "bottomLeft"]}
            total={adminTable?.count || 0}
            pageSize={PAGE_SIZE}
            current={page}
            showLessItems={true}
            showTotal={(total, range) => (
              <Text>
                Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
              </Text>
            )}
            itemRender={(_, type, originalElement) =>
              PAGE_SIZE >= adminTable?.count ? null : itemRender(_, type, originalElement, t)
            }
            showSizeChanger={false}
            showQuickJumper={false}
          />
        </div>
      </Flex>
      <RewardChartModal
        open={openRewardChart}
        close={() => setOpenRewardChart(false)}
        baId={chartBaId}
        userId={chartUserId}
      />
      <AlertsChartModal
        open={openAlertsChart}
        close={() => setOpenAlertsChart(false)}
        baId={chartBaId}
      />
    </Flex>
  )
}

export default AdminMainTable
